import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Star, Flame, Lightbulb, ChevronRight, Sparkles, Clock, GraduationCap, Zap } from 'lucide-react';
import type { B2bTraining } from '../../services/b2bAcademyApi';
import { INFO_BLAUW, INKOOP, LEERVORM, WKR_ORANJE, prijs } from './b2bStijl';

/** 16:9-cover met B2B inkoop-badge (linksboven), ROI-rating + 🔥 (rechtsboven) en vanaf-prijs (rechtsonder). */
const TegelCover: React.FC<{ training: B2bTraining; groot?: boolean }> = ({ training, groot }) => {
  const { t } = useTranslation('pages');
  const accent = training.rij_kleur;
  const inkoop = INKOOP[training.inkoop_type];
  const InkoopIcon = inkoop.icon;
  return (
    <div className="relative aspect-video overflow-hidden" style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}>
      {training.omslagfoto_url ? (
        <img src={training.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" draggable={false} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className={groot ? 'text-7xl' : 'text-5xl'}>{training.rij_emoji}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0A192F]/60 to-transparent" aria-hidden="true" />

      <span className={`absolute left-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-white font-bold text-gray-900 shadow-lg shadow-black/40 ${groot ? 'px-3 py-1.5 text-xs' : 'px-2.5 py-1 text-[11px]'}`}>
        <InkoopIcon className="h-3.5 w-3.5 text-[#0B1F3A]" aria-hidden="true" />
        {t(`b2bAcademy.inkoop.${training.inkoop_type}`, { defaultValue: inkoop.label })}
      </span>

      <div className="absolute right-2.5 top-2.5 flex items-center gap-1.5">
        {(training.populair || training.top_populair) && (
          <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[11px] font-semibold text-orange-300 backdrop-blur" title={training.top_populair ? t('b2bAcademy.tegel.topPopulairTitel', { defaultValue: 'Top 10% meest ingekochte B2B-trajecten op BeLagom' }) : undefined}>
            <Flame className="h-3.5 w-3.5 animate-pulse text-orange-400" aria-hidden="true" />
            {training.top_populair ? t('b2bAcademy.tegel.topPopulair', { defaultValue: 'Meest populair' }) : t('b2bAcademy.tegel.populair', { defaultValue: 'Populair' })}
          </span>
        )}
        {training.roi_rating && (
          <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[11px] font-bold text-white backdrop-blur" title={t('b2bAcademy.tegel.reviews', { defaultValue: '{{n}} beoordelingen van werkgevers', n: training.roi_rating.aantal })}>
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
            {training.roi_rating.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            <span className="font-medium text-slate-300">{t('b2bAcademy.tegel.roi', { defaultValue: 'ROI Rating' })}</span>
          </span>
        )}
      </div>

      <div className="absolute bottom-2.5 right-2.5 inline-flex items-baseline gap-1.5 rounded-lg bg-black/75 px-2.5 py-1 backdrop-blur">
        <span className="text-[11px] text-slate-300">{t('b2bAcademy.tegel.vanaf', { defaultValue: 'Vanaf' })}</span>
        <span className="text-sm font-extrabold text-white">{prijs(training.vanaf_prijs)}</span>
        <span className="text-[11px] text-slate-400">{t('b2bAcademy.tegel.perGroep', { defaultValue: 'per groep' })}</span>
      </div>
    </div>
  );
};

/** Donkerblauw informatievlak (.course-card-info-block): titel, opleider, vorm + duur en de rij-badge in wit/zilver. */
const TegelBalk: React.FC<{ training: B2bTraining; titelKnop?: React.ReactNode }> = ({ training, titelKnop }) => {
  const { t } = useTranslation('pages');
  const vorm = LEERVORM[training.leervorm];
  const Icon = vorm.icon;
  return (
    <div className="course-card-info-block px-3.5 pb-3 pt-2.5" style={{ backgroundColor: INFO_BLAUW }}>
      {titelKnop ?? <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">{training.titel}</h3>}
      <p className="mt-1 truncate text-xs text-slate-400">
        {t('b2bAcademy.tegel.opleider', { defaultValue: 'Opleider:' })} <span className="text-slate-100">{training.organisatie_naam}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="inline-flex min-w-0 items-center gap-1 text-slate-300">
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{t(`b2bAcademy.leervorm.${training.leervorm}`, { defaultValue: vorm.label })}</span>
        </span>
        {training.duur && (
          <>
            <span className="h-3.5 w-px shrink-0 bg-white/15" aria-hidden="true" />
            <span className="inline-flex min-w-0 items-center gap-1 text-slate-300">
              <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{training.duur}</span>
            </span>
          </>
        )}
        <span className="h-3.5 w-px shrink-0 bg-white/15" aria-hidden="true" />
        <span className="inline-flex items-center gap-1 font-medium" style={{ color: training.rij_kleur }}>
          {training.rij_emoji} {training.rij_label}
        </span>
      </div>
    </div>
  );
};

/** "The Business Callout": ROI one-liner, collectief voordeel, de fiscale label-tag en [⚡ Direct Anoniem Boeken]. */
const HoverPaneel: React.FC<{ training: B2bTraining; onBoek: () => void }> = ({ training, onBoek }) => {
  const { t } = useTranslation('pages');
  return (
    <div className="border-t border-white/10 px-3.5 pb-3.5 pt-3" style={{ backgroundColor: INFO_BLAUW }}>
      {training.short_pitch && (
        <p className="flex items-start gap-2 text-[13px] font-semibold leading-snug text-white">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <span className="line-clamp-2 flex-1">{training.short_pitch}</span>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </p>
      )}
      <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-[11px] font-medium text-amber-200">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        {t('b2bAcademy.tegel.voordeel', { defaultValue: 'Inclusief 5% BeLagom Collectief Voordeel' })}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
          style={training.wkr_belast ? { borderColor: `${WKR_ORANJE}80`, color: '#EEC566', backgroundColor: `${WKR_ORANJE}14` } : { borderColor: '#34d39966', color: '#6ee7b7', backgroundColor: '#34d39914' }}
        >
          {training.wkr_belast ? '⚠️' : '💸'} {training.fiscaal_label}
        </span>
        {training.certificaat && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/90 px-2 py-0.5 text-[11px] font-medium text-white">
            <GraduationCap className="h-3 w-3" aria-hidden="true" />
            {t('b2bAcademy.tegel.certificaat', { defaultValue: 'Certificaat inclusief' })}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onBoek();
        }}
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#E5A425] px-3 py-2 text-sm font-bold text-[#0A192F] shadow-[0_0_18px_rgba(229,164,37,.45)] transition hover:bg-[#F2B53A] hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <Zap className="h-4 w-4 fill-current" aria-hidden="true" />
        {t('b2bAcademy.tegel.directBoeken', { defaultValue: 'Direct Anoniem Boeken' })}
      </button>
    </div>
  );
};

const HOVER_VERTRAGING_MS = 280;
const SCHAAL = 1.06;

/**
 * B2B-venster: 16:9-cover + donkerblauw infovlak; hover = subtiele vergroting + rijkleur-rand,
 * Business Callout en [⚡ Direct Anoniem Boeken] (onBoek, valt terug op onOpen).
 */
export const B2bTegel: React.FC<{ training: B2bTraining; onOpen: (training: B2bTraining) => void; onBoek?: (training: B2bTraining) => void; className?: string }> = ({ training, onOpen, onBoek, className = '' }) => {
  const ref = useRef<HTMLElement>(null);
  const timer = useRef<number | null>(null);
  const [preview, setPreview] = useState<DOMRect | null>(null);
  const kanHoveren = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
  const stopTimer = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stopTimer, []);
  const toonPreview = useCallback(() => {
    if (ref.current) setPreview(ref.current.getBoundingClientRect());
  }, []);
  const sluit = useCallback(() => setPreview(null), []);

  return (
    <>
      <article
        ref={ref}
        onMouseEnter={() => {
          if (!kanHoveren()) return;
          stopTimer();
          timer.current = window.setTimeout(toonPreview, HOVER_VERTRAGING_MS);
        }}
        onMouseLeave={stopTimer}
        className={`relative shrink-0 snap-start overflow-hidden rounded-xl shadow-md shadow-slate-900/10 ring-1 transition duration-200 ${training.wkr_belast ? '' : 'ring-slate-900/5'} ${preview ? 'opacity-0' : ''} ${className}`}
        style={{ backgroundColor: INFO_BLAUW, ...(training.wkr_belast ? { boxShadow: `0 0 0 1px ${WKR_ORANJE}66, 0 4px 6px -1px rgba(10,25,47,.1)` } : {}) }}
      >
        <TegelCover training={training} />
        <TegelBalk
          training={training}
          titelKnop={
            <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">
              <button type="button" onClick={() => (kanHoveren() ? onOpen(training) : toonPreview())} className="text-left after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-sky-400">
                {training.titel}
              </button>
            </h3>
          }
        />
      </article>
      {preview && <HoverPreview training={training} anker={ref} rect={preview} onSluit={sluit} onOpen={() => onOpen(training)} onBoek={() => (onBoek ?? onOpen)(training)} />}
    </>
  );
};

const HoverPreview: React.FC<{ training: B2bTraining; anker: React.RefObject<HTMLElement | null>; rect: DOMRect; onSluit: () => void; onOpen: () => void; onBoek: () => void }> = ({ training, anker, rect, onSluit, onOpen, onBoek }) => {
  const { t } = useTranslation('pages');
  const paneel = useRef<HTMLDivElement>(null);
  const [actief, setActief] = useState(false);
  const [verschuiving, setVerschuiving] = useState(0);
  const accent = training.rij_kleur;
  const touch = typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches;
  const marge = (rect.width * (SCHAAL - 1)) / 2 + 12;
  const originX = rect.left < marge ? 'left' : window.innerWidth - rect.right < marge ? 'right' : 'center';

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => setActief(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  useEffect(() => {
    if (!actief) return;
    const id = window.setTimeout(() => {
      if (!paneel.current) return;
      const over = paneel.current.getBoundingClientRect().bottom - (window.innerHeight - 12);
      if (over > 0) setVerschuiving(-over);
    }, 320);
    return () => window.clearTimeout(id);
  }, [actief]);
  useEffect(() => {
    const sluit = () => {
      const nu = anker.current?.getBoundingClientRect();
      if (!nu || Math.abs(nu.left - rect.left) > 6 || Math.abs(nu.top - rect.top) > 6) onSluit();
    };
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && onSluit();
    window.addEventListener('scroll', sluit, true);
    window.addEventListener('resize', sluit);
    window.addEventListener('keydown', toets);
    return () => {
      window.removeEventListener('scroll', sluit, true);
      window.removeEventListener('resize', sluit);
      window.removeEventListener('keydown', toets);
    };
  }, [onSluit, anker, rect]);

  return createPortal(
    <>
      {touch && <div className="fixed inset-0 z-40" onClick={onSluit} aria-hidden="true" />}
      <div className="fixed z-50" style={{ left: rect.left, top: rect.top + verschuiving, width: rect.width }} onMouseLeave={touch ? undefined : onSluit}>
        <div
          ref={paneel}
          role="button"
          tabIndex={-1}
          onClick={onOpen}
          aria-label={t('b2bAcademy.tegel.openDetail', { defaultValue: 'Meer informatie over {{titel}}', titel: training.titel })}
          className="cursor-pointer overflow-hidden rounded-xl transition-[transform,box-shadow,translate] duration-300 ease-out"
          style={{
            backgroundColor: INFO_BLAUW,
            transformOrigin: `${originX} 35%`,
            transform: actief ? `scale(${SCHAAL})` : 'scale(1)',
            boxShadow: actief ? `0 0 0 1.5px ${accent}, 0 20px 40px -12px rgba(10,25,47,.45)` : '0 4px 6px -1px rgba(10,25,47,.1)',
          }}
        >
          <TegelCover training={training} />
          <TegelBalk training={training} />
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${actief ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
              <HoverPaneel training={training} onBoek={onBoek} />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default B2bTegel;
